import React, { useState, useRef, useEffect, useCallback } from "react";
import { streamPlanner } from "../utils/plannerStream";
import {
  P, PL, BORDER, ACCENT, INK, MUTED, LINE, SYNTH_ACCENT,
  CANVAS_BG, inp, lbl, outBtn, chip, truncate,
} from "./workflowTheme";

/**
 * Conversational front-end for the research-DAG planner agent.
 *
 * The endpoint is stateless, so this component owns the session: it keeps the
 * running `messages` list and the last plan, and sends both on every turn
 * (`current_plan` lets the agent edit the plan instead of rebuilding it).
 * Leaving the screen or refreshing starts a fresh session — that is expected.
 *
 * The planner only *designs*; saving stays with the builder, so a finished plan
 * is handed over through `onUsePlan`.
 */

const EXAMPLES = [
  "Build a template to assess governance quality and a 12-month valuation view.",
  "Design a workflow that screens a semiconductor name: demand cycle, competitive moat, and margin risk.",
  "Create a template for an earnings-driven thesis: call transcript read, guidance credibility, and estimate revisions.",
];

/** Tool labels arrive as "SearchAgents: phrase, phrase"; the end event carries just the name. */
const toolName = (label) => String(label || "").split(":")[0].trim().toLowerCase();

const markToolDone = (tools, label) => {
  const name = toolName(label);
  let i = tools.map(t => t.running && toolName(t.label) === name).lastIndexOf(true);
  if (i < 0) i = tools.map(t => t.running).lastIndexOf(true);
  if (i < 0) return tools;
  const next = [...tools];
  next[i] = { ...next[i], running: false };
  return next;
};

/** Cheap sanity check — the server validates, this only guards a truncated payload. */
const planShapeError = (plan) => {
  if (!plan || typeof plan !== "object") return "The plan payload was empty.";
  const nodes = Array.isArray(plan.nodes) ? plan.nodes : [];
  const agents = nodes.filter(n => n.node_type === "agent");
  const synths = nodes.filter(n => n.node_type === "synthesizer");
  if (agents.length === 0) return "The plan has no agent nodes.";
  if (synths.length !== 1) return `The plan has ${synths.length} synthesizer nodes — it needs exactly one.`;
  if (!plan.template?.name) return "The plan has no template name.";
  return null;
};

/** How many targets a selector decides between — 2+ means one choice among several. */
const selectorSize = (edges, selectorId) =>
  selectorId ? edges.filter(e => e.condition?.selector_id === selectorId).length : 1;

const planCounts = (plan) => {
  const nodes = Array.isArray(plan?.nodes) ? plan.nodes : [];
  return {
    agents: nodes.filter(n => n.node_type === "agent"),
    synth: nodes.find(n => n.node_type === "synthesizer") || null,
    edges: Array.isArray(plan?.edges) ? plan.edges : [],
    areas: Array.isArray(plan?.research_areas) ? plan.research_areas : [],
  };
};

const spinKeyframes = `@keyframes wfp-spin { to { transform: rotate(360deg) } }`;

const Dot = () => (
  <span style={{
    width: "9px", height: "9px", borderRadius: "50%", flexShrink: 0,
    border: `2px solid ${ACCENT}`, borderTopColor: "transparent",
    display: "inline-block", animation: "wfp-spin .7s linear infinite",
  }} />
);

// ─── one assistant turn (live or committed) ───────────────────────────────────

const AssistantTurn = ({ turn, defaultOpen }) => {
  const [showThink, setShowThink] = useState(!!defaultOpen);
  const { agents, edges, areas } = planCounts(turn.plan);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "88%" }}>
      {(turn.think || turn.tools.length > 0) && (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: "12px", background: "#f8fafc", overflow: "hidden" }}>
          <button
            onClick={() => setShowThink(s => !s)}
            style={{
              display: "flex", alignItems: "center", gap: "7px", width: "100%", textAlign: "left",
              padding: "8px 11px", background: "transparent", border: "none", cursor: "pointer",
              fontSize: "11px", fontWeight: 700, color: MUTED,
            }}
          >
            {turn.streaming ? <Dot /> : <span style={{ color: "#94a3b8" }}>✓</span>}
            <span>{turn.streaming ? "Planning…" : "Reasoning & searches"}</span>
            <span style={{ marginLeft: "auto", fontSize: "10px" }}>{showThink ? "▲" : "▼"}</span>
          </button>

          {showThink && (
            <div style={{ padding: "0 11px 10px" }}>
              {turn.think?.trim() && (
                <p style={{
                  margin: "0 0 8px", fontSize: "11px", lineHeight: 1.6, color: "#475569",
                  whiteSpace: "pre-wrap", borderLeft: `2px solid ${LINE}`, paddingLeft: "9px",
                }}>
                  {turn.think.trim()}
                </p>
              )}
              {turn.tools.map((t, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px",
                  fontSize: "10px", color: t.running ? ACCENT : MUTED,
                }}>
                  {t.running ? <Dot /> : <span>✓</span>}
                  <span style={{ ...truncate }}>{t.label || "Search"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {turn.content?.trim() && (
        <div style={{
          background: "#fff", border: `1px solid ${LINE}`, borderRadius: "12px",
          padding: "11px 13px", fontSize: "13px", lineHeight: 1.6, color: INK, whiteSpace: "pre-wrap",
        }}>
          {turn.content.trim()}
        </div>
      )}

      {turn.plan && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
          background: PL, border: "1px solid #c7d2fe", borderRadius: "10px", padding: "7px 11px",
          fontSize: "11px", color: P, fontWeight: 600,
        }}>
          <span>✦ Plan ready</span>
          <span style={{ color: ACCENT, fontWeight: 500 }}>
            {agents.length} agent{agents.length !== 1 ? "s" : ""} · {edges.length} edge{edges.length !== 1 ? "s" : ""} · {areas.length} area{areas.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {turn.error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px",
          padding: "9px 12px", fontSize: "12px", color: "#b91c1c", lineHeight: 1.5,
        }}>
          {turn.error}
        </div>
      )}

      {!turn.streaming && !turn.content?.trim() && !turn.plan && !turn.error && (
        <div style={{ fontSize: "12px", color: MUTED, fontStyle: "italic" }}>
          The planner returned no reply for this turn.
        </div>
      )}
    </div>
  );
};

// ─── plan preview ─────────────────────────────────────────────────────────────

const PlanPreview = ({ plan, onUse, onOpenJson }) => {
  const { agents, synth, edges, areas } = planCounts(plan);
  const nameOf = (id) => {
    const n = (plan.nodes || []).find(x => x.node_id === id);
    return n?.name || id;
  };
  const areaName = (gid) => areas.find(a => a.group_id === gid)?.name || gid || "—";
  const shapeError = planShapeError(plan);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ padding: "16px 18px 12px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <p style={{ fontSize: "11px", fontWeight: 700, color: P, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Proposed Template
        </p>
        <div style={{ fontSize: "15px", fontWeight: 700, color: INK }}>{plan.template?.name || "Untitled"}</div>
        {plan.template?.description && (
          <p style={{ margin: "5px 0 0", fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>
            {plan.template.description}
          </p>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
        {areas.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label style={lbl}>Research areas</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {areas.map(a => (
                <span key={a.group_id} style={{ ...chip, background: "#f1f5f9", color: "#334155", fontWeight: 600 }}>
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: "16px" }}>
          <label style={lbl}>Agents ({agents.length})</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {agents.map(n => (
              <div key={n.node_id} style={{ border: `1px solid ${LINE}`, borderRadius: "10px", padding: "8px 10px", background: "#fff" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: INK, ...truncate }}>{n.name}</div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px", ...truncate }}>
                  {n.role || "No role"} · {areaName(n.group_id)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {synth && (
          <div style={{ marginBottom: "16px" }}>
            <label style={lbl}>Synthesizer</label>
            <div style={{ border: "1px solid #ddd6fe", borderRadius: "10px", padding: "8px 10px", background: "#faf5ff" }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#4c1d95" }}>✦ {synth.name}</div>
              <div style={{ fontSize: "10px", color: SYNTH_ACCENT, marginTop: "2px" }}>
                {synth.synthesizer_kind || "investment_memo"}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: "14px" }}>
          <label style={lbl}>Edges ({edges.length})</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {edges.map((e, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: "6px",
                border: `1px solid ${LINE}`, borderRadius: "6px", padding: "5px 8px",
                fontSize: "11px", color: "#374151", background: "#fff",
              }}>
                <span
                  title={e.condition?.selector_id ? `Selector ${e.condition.selector_id}` : undefined}
                  style={{ ...chip, background: e.condition ? "#fef3c7" : "#f1f5f9", color: e.condition ? "#b45309" : MUTED }}
                >
                  {/* a selector deciding between 2+ targets is one choice, not n gates */}
                  {!e.condition ? "ALWAYS"
                    : selectorSize(edges, e.condition.selector_id) > 1
                      ? `PICK ${selectorSize(edges, e.condition.selector_id)}`
                      : "IF"}
                </span>
                <span style={{ flex: 1, minWidth: 0, ...truncate }}>
                  {nameOf(e.from)} → {nameOf(e.to)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onOpenJson}
          style={{ ...outBtn, width: "100%", padding: "6px 8px", fontSize: "11px" }}
        >
          View plan JSON
        </button>
      </div>

      <div style={{ padding: "12px 18px", borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
        {shapeError ? (
          <p style={{
            margin: "0 0 8px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
            padding: "8px 10px", fontSize: "11px", color: "#b91c1c", lineHeight: 1.5,
          }}>
            {shapeError} Ask the planner to fix it before opening the builder.
          </p>
        ) : (
          <p style={{ margin: "0 0 8px", fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>
            Opens in the builder, where you can adjust it and save. Nothing is saved yet.
          </p>
        )}
        <button
          onClick={onUse}
          disabled={!!shapeError}
          style={{
            width: "100%", padding: "9px", fontSize: "13px", fontWeight: 700,
            background: shapeError ? "#9ca3af" : P, color: "#fff", border: "none",
            borderRadius: "6px", cursor: shapeError ? "not-allowed" : "pointer",
          }}
        >
          Open in Builder
        </button>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const WorkflowPlanner = ({ onUsePlan }) => {
  /** Committed conversation: { role, content, think?, tools?, plan?, error? } */
  const [turns, setTurns] = useState([]);
  /** In-flight assistant turn, or null. */
  const [live, setLive] = useState(null);
  /** Latest plan — sent back as `current_plan` and rendered in the preview. */
  const [plan, setPlan] = useState(null);
  const [input, setInput] = useState("");
  const [fatalError, setFatalError] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const streaming = !!live;

  // keep the transcript pinned to the newest content while tokens arrive
  useEffect(() => {
    const box = scrollRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [turns, live]);

  // a pending request must not outlive the screen
  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(async (raw) => {
    const text = String(raw || "").trim();
    if (!text || abortRef.current) return;

    setFatalError(null);
    setInput("");

    const history = [
      ...turns.filter(t => t.content?.trim()).map(t => ({ role: t.role, content: t.content })),
      { role: "user", content: text },
    ];
    setTurns(prev => [...prev, { role: "user", content: text }]);

    // mutable accumulator: handlers fire faster than state settles
    const acc = { role: "assistant", content: "", think: "", tools: [], plan: null, error: null };
    const sync = () => setLive({ ...acc, tools: [...acc.tools], streaming: true });
    sync();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamPlanner({
        messages: history,
        currentPlan: plan,
        signal: controller.signal,
        handlers: {
          onThink: (delta) => { acc.think += delta; sync(); },
          onToolStart: (label) => { acc.tools = [...acc.tools, { label, running: true }]; sync(); },
          onToolEnd: (label) => { acc.tools = markToolDone(acc.tools, label); sync(); },
          onAnswer: (delta) => { acc.content += delta; sync(); },
          onAnswerEnd: (full) => { acc.content = full; sync(); },
          onTemplate: (json) => {
            try {
              const parsed = JSON.parse(json);
              acc.plan = parsed;
              setPlan(parsed);
              setShowJson(false);
            } catch {
              acc.error = "The planner returned a plan that could not be read — ask it to try again.";
            }
            sync();
          },
          onError: (message) => { acc.error = message || "The planner failed."; sync(); },
        },
      });
    } catch (err) {
      if (err?.name === "AbortError") acc.error = "Stopped before the planner finished.";
      else acc.error = err?.message || "The planner request failed.";
    } finally {
      // any tool still marked running was cut short
      acc.tools = acc.tools.map(t => ({ ...t, running: false }));
      abortRef.current = null;
      setLive(null);
      setTurns(prev => [...prev, { ...acc, tools: [...acc.tools], streaming: false }]);
    }
  }, [turns, plan]);

  const newSession = () => {
    abortRef.current?.abort();
    setTurns([]); setLive(null); setPlan(null);
    setInput(""); setFatalError(null); setShowJson(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const empty = turns.length === 0 && !live;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <style>{spinKeyframes}</style>

      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", gap: "12px",
        padding: "14px 18px", background: "#fff", border: `1px solid ${LINE}`, borderRadius: "12px",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "17px", fontWeight: 700, color: INK }}>Plan a template with AI</div>
          <div style={{ fontSize: "11px", color: MUTED, marginTop: "2px" }}>
            Describe the research question. The planner searches the agent library and proposes a validated DAG — it never saves.
          </div>
        </div>
        <span style={{ fontSize: "11px", color: MUTED, flexShrink: 0 }}>
          {turns.filter(t => t.role === "user").length} turn{turns.filter(t => t.role === "user").length !== 1 ? "s" : ""}
        </span>
        <button style={{ ...outBtn, flexShrink: 0 }} onClick={newSession} disabled={empty && !plan}>
          New session
        </button>
      </div>

      {/* transcript + preview */}
      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
        <div style={{
          flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column",
          background: CANVAS_BG, border: "1px solid rgba(199,210,254,0.7)", borderRadius: "16px",
          minHeight: "560px", maxHeight: "78vh",
        }}>
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "18px 20px", minHeight: 0 }}>
            {empty ? (
              <div style={{ padding: "28px 0", textAlign: "center" }}>
                <p style={{ fontSize: "13px", color: MUTED, margin: "0 0 16px", lineHeight: 1.6 }}>
                  Tell the planner what the template should produce. It designs the agents, research areas and
                  dependencies — you review and save.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "560px", margin: "0 auto" }}>
                  {EXAMPLES.map(ex => (
                    <button
                      key={ex}
                      onClick={() => send(ex)}
                      style={{
                        textAlign: "left", padding: "10px 13px", fontSize: "12px", lineHeight: 1.5,
                        background: "#fff", color: "#334155", border: `1px solid ${LINE}`,
                        borderRadius: "12px", cursor: "pointer",
                      }}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "11px", color: "#94a3b8", margin: "18px 0 0" }}>
                  The first request after a deploy is slower — the agent warms its search index once.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {turns.map((t, i) => (
                  t.role === "user" ? (
                    <div key={i} style={{ alignSelf: "flex-end", maxWidth: "84%" }}>
                      <div style={{
                        background: P, color: "#fff", borderRadius: "12px",
                        padding: "10px 13px", fontSize: "13px", lineHeight: 1.55, whiteSpace: "pre-wrap",
                      }}>
                        {t.content}
                      </div>
                    </div>
                  ) : (
                    <AssistantTurn key={i} turn={t} />
                  )
                ))}
                {live && <AssistantTurn turn={live} defaultOpen />}
              </div>
            )}
          </div>

          {/* composer */}
          <div style={{
            flexShrink: 0, borderTop: `1px solid ${LINE}`, padding: "12px 16px",
            background: "rgba(255,255,255,0.9)", borderRadius: "0 0 16px 16px",
          }}>
            {fatalError && (
              <p style={{
                margin: "0 0 8px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
                padding: "8px 10px", fontSize: "12px", color: "#b91c1c",
              }}>
                {fatalError}
              </p>
            )}
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
              <textarea
                style={{ ...inp, minHeight: "44px", maxHeight: "140px", resize: "vertical", flex: 1 }}
                placeholder={plan
                  ? "Refine the plan — e.g. “swap the DCF agent”, “add earnings-call analysis”…"
                  : "Describe the template you want…"}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
              {streaming ? (
                <button
                  onClick={() => abortRef.current?.abort()}
                  style={{
                    padding: "10px 18px", fontSize: "13px", fontWeight: 700, flexShrink: 0,
                    background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca",
                    borderRadius: "8px", cursor: "pointer",
                  }}
                >
                  Stop
                </button>
              ) : (
                <button
                  onClick={() => send(input)}
                  disabled={!input.trim()}
                  style={{
                    padding: "10px 20px", fontSize: "13px", fontWeight: 700, flexShrink: 0,
                    background: input.trim() ? P : "#cbd5e1", color: "#fff", border: "none",
                    borderRadius: "8px", cursor: input.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  Send
                </button>
              )}
            </div>
            <p style={{ margin: "6px 0 0", fontSize: "10px", color: "#94a3b8" }}>
              Enter to send · Shift+Enter for a new line{plan ? " · the current plan is sent along, so refinements edit it" : ""}
            </p>
          </div>
        </div>

        <div style={{
          width: "min(400px, 34vw)", flexShrink: 0, background: "#fff",
          border: `1px solid ${LINE}`, borderRadius: "16px",
          minHeight: "560px", maxHeight: "78vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {plan ? (
            <PlanPreview plan={plan} onUse={() => onUsePlan?.(plan)} onOpenJson={() => setShowJson(true)} />
          ) : (
            <div style={{ padding: "20px" }}>
              <p style={{ fontSize: "11px", fontWeight: 700, color: P, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Proposed Template
              </p>
              <p style={{ fontSize: "12px", color: MUTED, lineHeight: 1.6, margin: 0 }}>
                No plan yet. Once the planner proposes one it appears here — agents, research areas and edges —
                ready to open in the builder.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* raw plan JSON */}
      {showJson && plan && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}
          onClick={() => setShowJson(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "20px", width: "min(720px, 100%)", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: INK }}>Plan JSON</h3>
              <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1, padding: 0 }} onClick={() => setShowJson(false)}>×</button>
            </div>
            <pre style={{
              flex: 1, overflow: "auto", margin: 0, background: "#f8fafc",
              border: `1px solid ${LINE}`, borderRadius: "10px", padding: "12px",
              fontSize: "11px", lineHeight: 1.5, color: "#334155",
            }}>
              {JSON.stringify(plan, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowPlanner;
